import echo from '@/utils/dashboard/laravelEcho'
import store from '@/store'

const branchHandlers = new Map()
const staffHandlers = new Map()

let branchChannelName = null
let staffChannelName = null
let notificationHandlerAttached = false

export function getDashboardEcho() {
  return echo
}

/**
 * 初始化後台 dashboard 的 WebSocket 訂閱。
 * 可重複呼叫（例如切換分店時），會自動重新訂閱。
 */
export function initDashboardBroadcast({ staffId, branchId, permissions = {} } = {}) {
  if (!staffId || !branchId) {
    return
  }

  if (permissions.MY_NOTIFICATION) {
    subscribeBranchChannel(branchId, { withNotificationHandler: true })
  } else {
    unsubscribeBranchChannel()
  }

  subscribeStaffChannel(staffId)
}

export function destroyDashboardBroadcast() {
  unsubscribeBranchChannel()
  unsubscribeStaffChannel()
}

/**
 * 訂閱分店 channel 的自訂事件（供 #8874 等共用）。
 * @returns {Function} unsubscribe
 */
export function onBranchEvent(eventName, handler) {
  if (!branchHandlers.has(eventName)) {
    branchHandlers.set(eventName, new Set())
  }
  branchHandlers.get(eventName).add(handler)

  if (branchChannelName) {
    echo.private(branchChannelName).listen(eventName, handler)
  }

  return () => {
    branchHandlers.get(eventName)?.delete(handler)
    if (branchChannelName) {
      echo.private(branchChannelName).stopListening(eventName, handler)
    }
  }
}

/**
 * 訂閱 staff channel 的自訂事件（供 #8874 等共用）。
 * @returns {Function} unsubscribe
 */
export function onStaffEvent(eventName, handler) {
  if (!staffHandlers.has(eventName)) {
    staffHandlers.set(eventName, new Set())
  }
  staffHandlers.get(eventName).add(handler)

  if (staffChannelName) {
    echo.private(staffChannelName).listen(eventName, handler)
  }

  return () => {
    staffHandlers.get(eventName)?.delete(handler)
    if (staffChannelName) {
      echo.private(staffChannelName).stopListening(eventName, handler)
    }
  }
}

function subscribeBranchChannel(branchId, { withNotificationHandler = false } = {}) {
  const channelName = `branch.${branchId}`

  if (branchChannelName === channelName) {
    if (withNotificationHandler && !notificationHandlerAttached) {
      attachNotificationHandler(channelName)
    }
    return
  }

  unsubscribeBranchChannel()
  branchChannelName = channelName

  const channel = echo.private(channelName)

  if (withNotificationHandler) {
    attachNotificationHandler(channelName)
  }

  branchHandlers.forEach((handlers, eventName) => {
    handlers.forEach((handler) => channel.listen(eventName, handler))
  })
}

function attachNotificationHandler(channelName) {
  echo.private(channelName).listen('.notification.created', handleNotificationCreated)
  notificationHandlerAttached = true
}

function subscribeStaffChannel(staffId) {
  const channelName = `staff.${staffId}`

  if (staffChannelName === channelName) {
    return
  }

  unsubscribeStaffChannel()
  staffChannelName = channelName

  const channel = echo.private(channelName)

  staffHandlers.forEach((handlers, eventName) => {
    handlers.forEach((handler) => channel.listen(eventName, handler))
  })
}

function handleNotificationCreated(event) {
  const currentBranchId = store.state.general.currentBranch?.id

  if (!currentBranchId || !event.branch_ids?.includes(currentBranchId)) {
    return
  }

  store.dispatch('general/getUnreadNotificationCount')
}

function unsubscribeBranchChannel() {
  if (!branchChannelName) {
    return
  }

  echo.leave(branchChannelName)
  branchChannelName = null
  notificationHandlerAttached = false
}

function unsubscribeStaffChannel() {
  if (!staffChannelName) {
    return
  }

  echo.leave(staffChannelName)
  staffChannelName = null
}
